import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { leaveApi } from "./api";
import type {
  LeaveRequest,
  LeaveRequestPayload,
  LeaveRequestsListResult,
} from "./types";

export const leaveQueryKeys = {
  all: ["leave"] as const,
  list: ["leave", "list"] as const,
  detail: (id: number | null | undefined) => ["leave", "detail", id] as const,
};

export function useLeaveRequests() {
  return useQuery<LeaveRequestsListResult>({
    queryKey: leaveQueryKeys.list,
    queryFn: () => leaveApi.getLeaveRequests(),
    staleTime: 60_000,
  });
}

export function useLeaveRequest(id: number | null | undefined) {
  return useQuery<LeaveRequest>({
    queryKey: leaveQueryKeys.detail(id),
    queryFn: () => leaveApi.getLeaveRequestById(id!),
    enabled: typeof id === "number",
  });
}

export function useCreateLeaveRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: LeaveRequestPayload) => leaveApi.createLeaveRequest(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.list });
      queryClient.invalidateQueries({ queryKey: ["shifts", "scheduled"] });
    },
  });
}

export function useUpdateLeaveRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: LeaveRequestPayload;
    }) => leaveApi.updateLeaveRequest(id, payload),
    onSuccess: (leaveRequest) => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.list });
      queryClient.setQueryData(leaveQueryKeys.detail(leaveRequest.id), leaveRequest);
      queryClient.invalidateQueries({ queryKey: ["shifts", "scheduled"] });
    },
  });
}

export function useCancelLeaveRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => leaveApi.cancelLeaveRequest(id),
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.list });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ["shifts", "scheduled"] });
    },
  });
}
